import React, { useEffect, useMemo, useState } from 'react';
import {
  getAdminPendingRequests,
  getAdminAllRequests,
  approveServiceRequest,
  rejectServiceRequest,
  submitAdminFollowUp,
} from '../api/userApi';
import { getAdminAllReports, markReportReviewed } from '../api/AdminApi';
import '../Style/AdminDashboard.css';

const STATUS_STYLES = {
  PENDING: { color: '#f59e0b', label: 'Pending', icon: '⏳' },
  APPROVED: { color: '#6366f1', label: 'Approved', icon: '✅' },
  EXECUTED: { color: '#22c55e', label: 'Executed', icon: '🎯' },
  REJECTED: { color: '#ef4444', label: 'Rejected', icon: '❌' },
};

const TYPE_LABELS = {
  GOAL_CHECK: { label: 'Goal Check', icon: '🎯' },
  FREE_TEXT: { label: 'Custom Request', icon: '✏️' },
};

const formatMoney = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const formatDate = (date) => {
  if (!date) return 'Unknown date';

  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const AdminDashboard = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(null);

  const [activeTab, setActiveTab] = useState('requests'); // requests | reports
  const [requestView, setRequestView] = useState('pending'); // pending | all

  const [requests, setRequests] = useState([]);
  const [reports, setReports] = useState([]);

  const [loading, setLoading] = useState(false);
  const [rejectNotes, setRejectNotes] = useState({});
  const [followUpInputs, setFollowUpInputs] = useState({});

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportFilter, setReportFilter] = useState('UNREVIEWED'); // UNREVIEWED | REVIEWED | ALL

  const fetchDashboardData = async () => {
    setLoading(true);

    try {
      if (activeTab === 'reports') {
        const data = await getAdminAllReports();
        setReports(Array.isArray(data) ? data : []);
        return;
      }

      const data =
        requestView === 'all' ? await getAdminAllRequests() : await getAdminPendingRequests();

      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load admin dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (unlocked) {
      fetchDashboardData();
    }
  }, [unlocked, activeTab, requestView]);

  const handleUnlock = async () => {
    if (!passwordInput.trim()) {
      setPasswordError('Please enter the admin password.');
      return;
    }

    sessionStorage.setItem('adminPass', passwordInput);

    try {
      await getAdminPendingRequests();
      setUnlocked(true);
      setPasswordError(null);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        setPasswordError('Invalid admin password. Access denied.');
      } else {
        setPasswordError('Error connecting to server.');
      }

      sessionStorage.removeItem('adminPass');
    }
  };

  const handleApprove = async (requestId) => {
    const note = rejectNotes[requestId] || null;

    try {
      await approveServiceRequest(requestId, note);
      await fetchDashboardData();
    } catch (err) {
      console.error('Approve failed', err);
    }
  };

  const handleReject = async (requestId) => {
    const note = rejectNotes[requestId];

    try {
      await rejectServiceRequest(requestId, note);

      setRejectNotes((prev) => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });

      await fetchDashboardData();
    } catch (err) {
      console.error('Reject failed', err);
    }
  };

  const handleFollowUp = async (requestId, reApprove) => {
    const followUp = followUpInputs[requestId];

    if (!followUp?.trim()) return;

    try {
      await submitAdminFollowUp(requestId, followUp, reApprove);

      setFollowUpInputs((prev) => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });

      await fetchDashboardData();
    } catch (err) {
      console.error('Follow up failed', err);
    }
  };

  const handleMarkReportReviewed = async (reportId) => {
    try {
      await markReportReviewed(reportId);
      await fetchDashboardData();
    } catch (err) {
      console.error('Failed to mark report reviewed', err);
    }
  };

  const requestCounts = useMemo(() => {
    return {
      total: requests.length,
      pending: requests.filter((r) => r.status === 'PENDING').length,
      executed: requests.filter((r) => r.status === 'EXECUTED').length,
      rejected: requests.filter((r) => r.status === 'REJECTED').length,
    };
  }, [requests]);

  const reportCounts = useMemo(() => {
    return {
      total: reports.length,
      unreviewed: reports.filter((r) => !r.reviewed).length,
      reviewed: reports.filter((r) => r.reviewed).length,
    };
  }, [reports]);

  const filteredRequests = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return requests.filter((request) => {
      const matchesStatus = statusFilter === 'ALL' || request.status === statusFilter;

      const matchesType = typeFilter === 'ALL' || request.requestType === typeFilter;

      const searchableText = [
        request.userFirstName,
        request.userLastName,
        request.userEmail,
        request.requestType,
        request.status,
        request.goalTitle,
        request.targetDate,
        request.customMessage,
        request.adminNote,
        request.userReply,
        request.adminFollowUp,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !normalizedSearch || searchableText.includes(normalizedSearch);

      return matchesStatus && matchesType && matchesSearch;
    });
  }, [requests, statusFilter, typeFilter, searchTerm]);

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      if (reportFilter === 'UNREVIEWED') return !report.reviewed;
      if (reportFilter === 'REVIEWED') return report.reviewed;
      return true;
    });
  }, [reports, reportFilter]);

  if (!unlocked) {
    return (
      <div className="admin-password-gate">
        <div className="admin-gate-card">
          <h2>🔒 Admin Access</h2>
          <p>Enter the admin password to continue.</p>

          <input
            type="password"
            className="admin-gate-input"
            placeholder="Admin password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
          />

          {passwordError && <p className="donation-error">{passwordError}</p>}

          <button className="donate-btn" onClick={handleUnlock}>
            Unlock
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p className="admin-subtitle">Manage service requests, user reports, and follow-ups.</p>
        </div>

        <button className="admin-refresh-btn" onClick={fetchDashboardData}>
          Refresh
        </button>
      </div>

      <div className="admin-overview-grid">
        <div className="admin-overview-card">
          <span className="overview-number">{requestCounts.pending}</span>
          <span className="overview-label">Pending Requests</span>
        </div>

        <div className="admin-overview-card">
          <span className="overview-number">{requestCounts.executed}</span>
          <span className="overview-label">Executed Requests</span>
        </div>

        <div className="admin-overview-card">
          <span className="overview-number">{requestCounts.rejected}</span>
          <span className="overview-label">Rejected Requests</span>
        </div>

        <div className="admin-overview-card">
          <span className="overview-number">{reportCounts.unreviewed}</span>
          <span className="overview-label">Unreviewed Reports</span>
        </div>
      </div>

      <div className="admin-main-tabs">
        <button
          className={`admin-tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Service Requests
        </button>

        <button
          className={`admin-tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          User Reports
          {reportCounts.unreviewed > 0 && (
            <span className="tab-count">{reportCounts.unreviewed}</span>
          )}
        </button>
      </div>

      {activeTab === 'requests' && (
        <>
          <div className="admin-toolbar">
            <div className="admin-toggle">
              <button
                className={`toggle-btn ${requestView === 'pending' ? 'active' : ''}`}
                onClick={() => setRequestView('pending')}
              >
                Pending Only
              </button>

              <button
                className={`toggle-btn ${requestView === 'all' ? 'active' : ''}`}
                onClick={() => setRequestView('all')}
              >
                All Requests
              </button>
            </div>

            <div className="admin-filters">
              <input
                className="admin-search-input"
                type="text"
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              <select
                className="admin-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="EXECUTED">Executed</option>
                <option value="REJECTED">Rejected</option>
              </select>

              <select
                className="admin-filter-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="ALL">All Types</option>
                <option value="GOAL_CHECK">Goal Check</option>
                <option value="FREE_TEXT">Custom Request</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading-text">Loading requests...</div>
          ) : filteredRequests.length === 0 ? (
            <div className="admin-empty">No matching requests found.</div>
          ) : (
            <div className="admin-request-list">
              {filteredRequests.map((req) => {
                const status = STATUS_STYLES[req.status] || {
                  color: '#64748b',
                  label: req.status,
                  icon: '•',
                };

                const type = TYPE_LABELS[req.requestType] || {
                  label: req.requestType,
                  icon: '📄',
                };

                return (
                  <div key={req.id} className="admin-request-card">
                    <div className="admin-card-top">
                      <div className="admin-user-info">
                        <span className="admin-user-name">
                          {req.userFirstName} {req.userLastName}
                        </span>
                        <span className="admin-user-email">{req.userEmail}</span>
                      </div>

                      <div className="admin-card-right">
                        <div className="admin-donation-badge">
                          {formatMoney(req.donationAmount)}
                        </div>

                        <span
                          className="admin-status-pill"
                          style={{
                            color: status.color,
                            borderColor: status.color,
                          }}
                        >
                          {status.icon} {status.label}
                        </span>
                      </div>
                    </div>

                    <div className="admin-card-body">
                      <span className="admin-request-type">
                        {type.icon} {type.label}
                      </span>

                      {req.requestType === 'GOAL_CHECK' ? (
                        <p className="admin-request-detail">
                          Goal: <strong>{req.goalTitle}</strong> — Date:{' '}
                          <strong>{req.targetDate}</strong>
                        </p>
                      ) : (
                        <p className="admin-request-detail">
                          {req.customMessage || 'No message provided.'}
                        </p>
                      )}
                    </div>

                    <div className="admin-card-meta">
                      <span>Created {formatDate(req.createdAt)}</span>
                      {req.resolvedAt && <span>Resolved {formatDate(req.resolvedAt)}</span>}
                    </div>

                    {req.adminNote && (
                      <div className="admin-saved-note">
                        <strong>
                          {req.adminNote.toLowerCase().includes('automatically')
                            ? 'System Note:'
                            : 'Admin Note:'}
                        </strong>{' '}
                        {req.adminNote}
                      </div>
                    )}

                    {req.status === 'PENDING' && (
                      <div className="admin-actions">
                        <textarea
                          className="admin-note-input"
                          placeholder="Optional admin note..."
                          rows={2}
                          value={rejectNotes[req.id] || ''}
                          onChange={(e) =>
                            setRejectNotes((prev) => ({
                              ...prev,
                              [req.id]: e.target.value,
                            }))
                          }
                        />

                        <div className="admin-action-btns">
                          <button
                            className="admin-approve-btn"
                            onClick={() => handleApprove(req.id)}
                          >
                            ✅ Approve & Execute
                          </button>

                          <button className="admin-reject-btn" onClick={() => handleReject(req.id)}>
                            ❌ Reject
                          </button>
                        </div>
                      </div>
                    )}

                    {req.status === 'REJECTED' && req.userReply && !req.adminFollowUp && (
                      <div className="admin-follow-up-section">
                        <div className="history-user-reply">
                          <span className="reply-label">User replied:</span> {req.userReply}
                        </div>

                        <textarea
                          className="admin-note-input"
                          placeholder="Your follow-up response..."
                          rows={2}
                          value={followUpInputs[req.id] || ''}
                          onChange={(e) =>
                            setFollowUpInputs((prev) => ({
                              ...prev,
                              [req.id]: e.target.value,
                            }))
                          }
                        />

                        <div className="admin-action-btns">
                          <button
                            className="admin-approve-btn"
                            onClick={() => handleFollowUp(req.id, true)}
                          >
                            ✅ Respond & Approve
                          </button>

                          <button
                            className="admin-reject-btn"
                            onClick={() => handleFollowUp(req.id, false)}
                          >
                            💬 Respond Only
                          </button>
                        </div>
                      </div>
                    )}

                    {req.adminFollowUp && (
                      <div className="history-note">
                        <span className="reply-label">Your follow-up:</span> {req.adminFollowUp}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === 'reports' && (
        <>
          <div className="admin-toolbar">
            <div className="admin-toggle">
              <button
                className={`toggle-btn ${reportFilter === 'UNREVIEWED' ? 'active' : ''}`}
                onClick={() => setReportFilter('UNREVIEWED')}
              >
                Unreviewed
              </button>

              <button
                className={`toggle-btn ${reportFilter === 'REVIEWED' ? 'active' : ''}`}
                onClick={() => setReportFilter('REVIEWED')}
              >
                Reviewed
              </button>

              <button
                className={`toggle-btn ${reportFilter === 'ALL' ? 'active' : ''}`}
                onClick={() => setReportFilter('ALL')}
              >
                All Reports
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-text">Loading reports...</div>
          ) : filteredReports.length === 0 ? (
            <div className="admin-empty">No reports found.</div>
          ) : (
            <div className="admin-request-list">
              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  className={`admin-request-card ${report.reviewed ? 'reviewed-card' : ''}`}
                >
                  <div className="admin-card-top">
                    <div className="admin-user-info">
                      <span className="admin-user-name">
                        Reported: {report.reportedFirstName} {report.reportedLastName}
                      </span>
                      <span className="admin-user-email">{report.reportedEmail}</span>
                    </div>

                    <span className="report-reason-badge">{report.reason?.replace(/_/g, ' ')}</span>
                  </div>

                  <div className="admin-card-body">
                    <span className="admin-user-name">
                      By: {report.reporterFirstName} {report.reporterLastName}
                    </span>

                    <span className="admin-user-email">{report.reporterEmail}</span>

                    {report.message && (
                      <p className="admin-request-detail">User Message: "{report.message}"</p>
                    )}
                  </div>

                  <div className="admin-card-meta">
                    <span>Reason: {report.reason?.replace(/_/g, ' ')}</span>
                    <span>{report.reviewed ? '✅ Reviewed' : '⚠️ Unreviewed'}</span>
                    <span>{formatDate(report.createdAt)}</span>
                  </div>

                  {!report.reviewed && (
                    <div className="admin-actions">
                      <button
                        className="admin-approve-btn"
                        onClick={() => handleMarkReportReviewed(report.id)}
                      >
                        ✅ Mark Reviewed
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
